'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Popover,
  ScrollShadow,
  Chip,
  Tooltip,
  Spinner,
  useOverlayState,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useAuthenticatedApi } from '@/lib/useAuthenticatedApi';
import { getEmbeddedSession, isSessionValid } from '@/lib/embeddedSessionUtils';
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
} from '@/lib/notificationsApi';
import type { NotificationReadDto, NotificationCategory, NotificationType } from '@/types';
import DOMPurify from 'dompurify';
import PushNotificationSettings from './PushNotificationSettings';

const POLL_INTERVAL_MS = 30_000;       // Fallback poll interval (SSE unavailable)
const SSE_RECONNECT_BASE_MS = 2_000;   // Base reconnect delay after disconnect
const SSE_RECONNECT_MAX_MS = 60_000;   // Cap reconnect backoff at 1 minute
const SSE_URL = '/api/v1/notifications/stream';

/** Returns true when the browser can consume a fetch ReadableStream (SSE via fetch). */
function supportsStreamingSse(): boolean {
  return (
    typeof ReadableStream !== 'undefined' &&
    typeof TextDecoder !== 'undefined'
  );
}

type AuthenticatedFetch = <T = unknown>(endpoint: string, options?: RequestInit) => Promise<T>;

// ── Shared connection singleton ──────────────────────────────────────────────
// The sidebar renders several NotificationBell instances at once (collapsed rail + expanded
// panel), and the mobile drawer unmounts/remounts its copies on every open/close. Keeping the
// SSE connection and unread-count state at module scope (rather than per-instance refs) means
// only ONE real connection/fetch exists per tab no matter how many bells mount or remount —
// mounted instances just subscribe to broadcasts and ref-count how many still want it alive.
let sseSubscriberCount = 0;
let sseAbortController: AbortController | null = null;
let sseReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let ssePollTimer: ReturnType<typeof setInterval> | null = null;
let sseReconnectDelay = SSE_RECONNECT_BASE_MS;
let sseAuthRetryCount = 0;
let sharedAccessToken: string | undefined;
let sharedIsAuthenticated = false;
let sharedUnreadCount = 0;
const unreadCountListeners = new Set<(count: number) => void>();
const notificationPushListeners = new Set<(notification: NotificationReadDto) => void>();

function broadcastUnreadCount(count: number) {
  sharedUnreadCount = count;
  unreadCountListeners.forEach((listener) => listener(count));
}

function broadcastNotification(notification: NotificationReadDto) {
  notificationPushListeners.forEach((listener) => listener(notification));
}

async function sharedRefreshUnreadCount(authenticatedFetch: AuthenticatedFetch) {
  if (!sharedIsAuthenticated) return;
  try {
    const count = await fetchUnreadCount(authenticatedFetch);
    broadcastUnreadCount(count);
  } catch (err) {
    // 403 = user has no notification permissions; warn rather than error since it's expected for some roles
    if ((err as Error)?.message?.includes('403')) {
      console.warn('NotificationBell: no permissions for this user');
      return;
    }
    console.error('[NotificationBell] Failed to fetch unread count:', err);
  }
}

function sharedConnectSse(authenticatedFetch: AuthenticatedFetch) {
  if (sseSubscriberCount === 0) return; // last subscriber left before a scheduled reconnect fired

  sseAbortController?.abort();
  const controller = new AbortController();
  sseAbortController = controller;
  const { signal } = controller;

  let buffer = '';
  let currentEventName = '';

  const processLine = (line: string) => {
    if (line.startsWith(':')) {
      // SSE comment / heartbeat — reset reconnect delay on successful heartbeat
      sseReconnectDelay = SSE_RECONNECT_BASE_MS;
      return;
    }
    if (line.startsWith('event:')) {
      currentEventName = line.slice(6).trim();
      return;
    }
    if (line.startsWith('data:')) {
      const raw = line.slice(5).trim();
      try {
        const payload = JSON.parse(raw);
        // Ignore the connection handshake event
        if (payload?.type === 'connected') {
          currentEventName = '';
          return;
        }
        if (currentEventName === 'unread_count' || currentEventName === 'unread-count') {
          broadcastUnreadCount(payload.count ?? payload.unreadCount ?? 0);
        } else if (payload._id) {
          // Named event: notification — OR unnamed default data: event (from backend)
          broadcastNotification(payload);
          if (!payload.isRead) {
            broadcastUnreadCount(sharedUnreadCount + 1);
          }
        }
      } catch {
        // Malformed JSON — ignore
      }
      currentEventName = '';
      return;
    }
    if (line === '') {
      // Empty line = end of event block, reset
      currentEventName = '';
    }
  };

  const run = async () => {
    // Check for embedded session first (Salesforce embedded mode)
    let embeddedSessionId: string | null = null;
    if (typeof window !== 'undefined') {
      const embeddedSession = getEmbeddedSession();
      if (embeddedSession) {
        embeddedSessionId = embeddedSession.sessionId || null;
      }
    }

    const token = sharedAccessToken;

    if (!embeddedSessionId && !token) {
      // No authentication available yet; the subscribing effect re-runs when it appears
      return;
    }

    try {
      const headers: HeadersInit = {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };

      if (embeddedSessionId) {
        headers['X-Embedded-Session'] = embeddedSessionId;
      } else if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(SSE_URL, { headers, signal });

      if (response.status === 401) {
        // Token may be invalid or a transient race condition occurred (e.g. server restart
        // briefly rejecting valid tokens). Retry up to 2 times with backoff before hard-stopping.
        if (sseAuthRetryCount < 2) {
          sseAuthRetryCount += 1;
          if (sseSubscriberCount > 0 && !signal.aborted) {
            sseReconnectTimer = setTimeout(() => {
              sseReconnectDelay = Math.min(sseReconnectDelay * 2, SSE_RECONNECT_MAX_MS);
              sharedConnectSse(authenticatedFetch);
            }, sseReconnectDelay);
          }
        } else {
          sseAuthRetryCount = 0;
        }
        return;
      }

      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      sseReconnectDelay = SSE_RECONNECT_BASE_MS; // reset on successful connect
      sseAuthRetryCount = 0; // reset auth retry counter on successful connect

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // Keep incomplete last line in buffer
        for (const line of lines) {
          processLine(line);
        }
      }
    } catch (err) {
      if (signal.aborted || sseSubscriberCount === 0) return; // intentional close
      console.warn('[NotificationBell] SSE disconnected, reconnecting…', err);
    }

    // Reconnect with exponential backoff (unless no one needs it anymore)
    if (sseSubscriberCount > 0 && !signal.aborted) {
      sseReconnectTimer = setTimeout(() => {
        sseReconnectDelay = Math.min(sseReconnectDelay * 2, SSE_RECONNECT_MAX_MS);
        sharedConnectSse(authenticatedFetch);
      }, sseReconnectDelay);
    }
  };

  run();
}

const CATEGORY_ICON: Record<NotificationCategory, string> = {
  support: 'solar:ticket-sale-linear',
  system: 'solar:settings-linear',
  flow: 'solar:routing-linear',
  general: 'solar:bell-linear',
};

const CATEGORY_COLOR: Record<NotificationCategory, string> = {
  support: 'warning',
  system: 'primary',
  flow: 'secondary',
  general: 'default',
};

const CHIP_COLOR: Record<NotificationCategory, 'default' | 'accent' | 'warning'> = {
  support: 'warning',
  system: 'accent',
  flow: 'default',
  general: 'default',
};

/** Maps notification `type` to a left-border / dot colour class */
const TYPE_DOT: Record<NotificationType, string> = {
  info: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-danger',
};

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface NotificationBellProps {
  /** Additional classes for the trigger button wrapper div */
  className?: string;
  /** Additional classes forwarded to the inner trigger Button (e.g. 'w-12 h-12' to match sidebar icon sizing) */
  buttonClassName?: string;
  /** Icon width in px. Defaults to 20. Use 24 to match sidebar icon siblings. */
  iconWidth?: number;
  /** Button size variant. Defaults to md. */
  buttonSize?: 'sm' | 'md' | 'lg';
}

export default function NotificationBell({ className, buttonClassName, iconWidth = 20, buttonSize = 'md' }: NotificationBellProps) {
  const { authenticatedFetch, session } = useAuthenticatedApi();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationReadDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread'>('unread');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Track both NextAuth and embedded auth
  
  // Push notification settings modal
  const { isOpen: isSettingsOpen, open: onSettingsOpen, close: onSettingsClose } = useOverlayState();

  // Memoize the access token to create a stable reference that only changes when the actual token value changes
  const accessToken = useMemo(() => session?.accessToken as string | undefined, [session?.accessToken]);

  // Keep the shared token/auth state in sync with whichever instance last observed it
  useEffect(() => {
    sharedAccessToken = accessToken;
    
    // Check auth status whenever token changes
    const checkAuth = () => {
      let authStatus = false;

      if (accessToken) {
        authStatus = true;
      } else if (typeof window !== 'undefined') {
        const embeddedSession = getEmbeddedSession();
        if (embeddedSession && isSessionValid(embeddedSession)) {
          authStatus = true;
        }
      }

      sharedIsAuthenticated = authStatus;
      // Only update if changed to prevent re-render loops
      // Compare against the current state value, not the ref
      if (isAuthenticated !== authStatus) {
        console.log('[NotificationBell] Auth status changed:', authStatus);
        setIsAuthenticated(authStatus);
      }
    };

    checkAuth();
    
    // Listen for storage events (for embedded session changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'embeddedSession') {
        checkAuth();
      }
    };
    
    // Listen for custom embedded session events
    const handleEmbeddedSessionChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('embeddedSessionChanged', handleEmbeddedSessionChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('embeddedSessionChanged', handleEmbeddedSessionChange);
    };
    // isAuthenticated intentionally omitted — checkAuth() reads it via closure to decide whether
    // to update state, re-running this effect on every change would be redundant/circular.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // ── Subscribe to the shared SSE connection / unread-count broadcasts ──────
  // Only the first subscriber across the whole tab actually opens a connection or fetch;
  // everyone else just mirrors the shared state. See the singleton block near the top of
  // this file for why this needs to live outside any single component instance.
  useEffect(() => {
    if (!isAuthenticated) return;

    const onUnreadCount = (count: number) => setUnreadCount(count);
    const onNotification = (notification: NotificationReadDto) => {
      setNotifications((prev) => {
        const exists = prev.some((n) => n._id === notification._id);
        return exists ? prev : [notification, ...prev];
      });
    };

    unreadCountListeners.add(onUnreadCount);
    notificationPushListeners.add(onNotification);
    setUnreadCount(sharedUnreadCount); // sync immediately with whatever's already known

    sseSubscriberCount += 1;
    if (sseSubscriberCount === 1) {
      // First subscriber — actually establish the shared connection
      sharedRefreshUnreadCount(authenticatedFetch);
      if (supportsStreamingSse()) {
        sharedConnectSse(authenticatedFetch);
      } else {
        // Polling fallback for browsers without ReadableStream
        ssePollTimer = setInterval(() => sharedRefreshUnreadCount(authenticatedFetch), POLL_INTERVAL_MS);
      }
    }

    return () => {
      unreadCountListeners.delete(onUnreadCount);
      notificationPushListeners.delete(onNotification);
      sseSubscriberCount -= 1;
      if (sseSubscriberCount === 0) {
        sseAbortController?.abort();
        sseAbortController = null;
        if (sseReconnectTimer) clearTimeout(sseReconnectTimer);
        if (ssePollTimer) clearInterval(ssePollTimer);
        ssePollTimer = null;
      }
    };
  }, [isAuthenticated, authenticatedFetch]);

  // Load notifications when popover opens
  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = activeFilter === 'unread' ? { isRead: false, limit: 20 } : { limit: 20 };
      const res = await fetchNotifications(params, authenticatedFetch);
      console.log('[NotificationBell] loadNotifications response:', res);
      const feed = res.data;
      setNotifications(feed?.items ?? []);
      setUnreadCount(feed?.unreadCount ?? 0);
    } catch (err) {
      console.error('[NotificationBell] Failed to load notifications:', err);
      // keep existing list on error
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch, activeFilter]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  const handleMarkRead = useCallback(
    async (id: string) => {
      try {
        await markNotificationRead(id, authenticatedFetch);
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
        );
        broadcastUnreadCount(Math.max(0, sharedUnreadCount - 1));
      } catch {
        // ignore
      }
    },
    [authenticatedFetch]
  );

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead(authenticatedFetch);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      broadcastUnreadCount(0);
    } catch {
      // ignore
    }
  }, [authenticatedFetch]);

  const handleDismiss = useCallback(
    async (id: string) => {
      try {
        const wasUnread = notifications.find((n) => n._id === id)?.isRead === false;
        await dismissNotification(id, authenticatedFetch);
        setNotifications((prev) => prev.filter((n) => n._id !== id));
        if (wasUnread) broadcastUnreadCount(Math.max(0, sharedUnreadCount - 1));
      } catch {
        // ignore
      }
    },
    [authenticatedFetch, notifications]
  );

  /** Navigate to the notification's link and mark it read */
  const handleNotificationClick = useCallback(
    async (n: NotificationReadDto) => {
      if (!n.isRead) {
        handleMarkRead(n._id);
      }
      if (n.link) {
        setIsOpen(false);
        window.open(n.link, '_blank', 'noopener,noreferrer');
      }
    },
    [handleMarkRead]
  );

  const visibleNotifications =
    activeFilter === 'unread' ? notifications.filter((n) => !n.isRead) : notifications;

  return (
    <>
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <Popover.Trigger>
        <Button
         
          size={buttonSize}
          variant="ghost"
          className={`relative overflow-visible ${buttonClassName || ''} ${className || ''}`}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Icon
            icon={unreadCount > 0 ? 'solar:bell-bing-bold-duotone' : 'solar:bell-linear'}
            width={iconWidth}
            height={iconWidth}
            className={unreadCount > 0 ? 'text-accent' : 'text-muted'}
            style={{ width: `${iconWidth}px`, height: `${iconWidth}px` }}
          />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-danger text-white text-[10px] font-semibold rounded-full pointer-events-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </Popover.Trigger>

      <Popover.Content
        placement="bottom end"
        className="p-0 w-[400px] max-w-[calc(100vw-16px)] overflow-hidden"
      >
        <div className="flex flex-col h-[480px]">
        {/* Header */}
        <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Tooltip>
                <Tooltip.Trigger>
                  <Button
                    size="sm"
                    variant="ghost"
                   
                    onPress={handleMarkAllRead}
                    aria-label="Mark all as read"
                  >
                    <Icon icon="solar:check-read-linear" width={18} />
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content>Mark all as read</Tooltip.Content>
              </Tooltip>
            )}
            <Tooltip>
              <Tooltip.Trigger>
                <Button
                  size="sm"
                  variant="ghost"
                 
                  onPress={() => {
                    setIsOpen(false); // Close notification popover
                    onSettingsOpen();
                  }}
                  aria-label="Push notification settings"
                >
                  <Icon icon="solar:settings-linear" width={18} />
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>Push notification settings</Tooltip.Content>
            </Tooltip>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex-none flex justify-center gap-2 px-4 pt-2 pb-1">
          <Button
            size="sm"
            variant={activeFilter === 'unread' ? 'primary' : 'secondary'}
            onPress={() => setActiveFilter('unread')}
            className="rounded-full min-w-0"
          >
            Unread
            {unreadCount > 0 && (
              <Chip size="sm" variant="soft" color="danger" className="ml-1.5 h-4 px-1.5">
                <Chip.Label className="text-[10px]">{unreadCount}</Chip.Label>
              </Chip>
            )}
          </Button>
          <Button
            size="sm"
            variant={activeFilter === 'all' ? 'primary' : 'secondary'}
            onPress={() => setActiveFilter('all')}
            className="rounded-full"
          >
            All
          </Button>
        </div>

        {/* List */}
        <ScrollShadow className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Spinner size="sm" />
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4 gap-2 text-center">
              <Icon icon="solar:bell-off-linear" width={32} className="text-muted" />
              <p className="text-sm text-muted">
                {activeFilter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-Separator">
              {visibleNotifications.map((n) => (
                <div
                  key={n._id}
                  className={`flex gap-3 px-4 py-3 group transition-colors ${
                    n.link ? 'cursor-pointer' : ''
                  } hover:bg-muted-50 ${
                    !n.isRead ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''
                  }`}
                  onClick={() => handleNotificationClick(n)}
                >
                  {/* Category icon */}
                  <div className="flex-none mt-0.5">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center bg-${
                        CATEGORY_COLOR[n.category] === 'default' ? 'default-100' : `${CATEGORY_COLOR[n.category]}-100`
                      }`}
                    >
                      <Icon
                        icon={CATEGORY_ICON[n.category] ?? 'solar:bell-linear'}
                        width={16}
                        className={`text-${CATEGORY_COLOR[n.category]}-500`}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1.5">
                      {/* Type-intent dot (info/success/warning/error) */}
                      <span
                        className={`mt-1.5 flex-none w-1.5 h-1.5 rounded-full ${TYPE_DOT[n.type] ?? 'bg-primary'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-xs leading-5 font-semibold truncate ${
                            !n.isRead ? 'text-foreground' : 'text-muted'
                          }`}
                        >
                          {n.title}
                        </p>
                        {n.message && (() => {
                          const isExpanded = expandedMessages.has(n._id);
                          const hasHtml = /<[a-z][\s\S]*>/i.test(n.message);
                          const sanitized = DOMPurify.sanitize(hasHtml ? n.message : n.message.replace(/\n/g, '<br />'));
                          return (
                            <div className="mt-0.5 overflow-hidden">
                              <p
                                className={`text-xs leading-4 text-muted overflow-wrap-anywhere ${isExpanded ? '' : 'line-clamp-2'}`}
                                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                dangerouslySetInnerHTML={{ __html: sanitized }}
                              />
                              {n.message.length > 80 && (
                                <button
                                  type="button"
                                  className="text-[10px] text-accent mt-0.5 hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedMessages((prev) => {
                                      const next = new Set(prev);
                                      if (isExpanded) { next.delete(n._id); } else { next.add(n._id); }
                                      return next;
                                    });
                                  }}
                                >
                                  {isExpanded ? 'Show less' : 'Show more'}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Chip
                        size="sm"
                        variant="soft"
                        color={CHIP_COLOR[n.category]}
                        className="h-4"
                      >
                        <Chip.Label className="text-[10px] px-1">{n.category}</Chip.Label>
                      </Chip>
                      <span className="text-[10px] text-muted">{formatRelativeTime(n.createdAt)}</span>
                      {n.link && (
                        <span className="text-[10px] text-accent flex items-center gap-0.5">
                          <Icon icon="solar:arrow-right-linear" width={10} />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div
                    className="flex-none flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!n.isRead && (
                      <Tooltip>
                        <Tooltip.Trigger>
                          <Button
                           
                            size="sm"
                            variant="ghost"
                            onPress={() => handleMarkRead(n._id)}
                            aria-label="Mark as read"
                            className="h-6 w-6 min-w-0"
                          >
                            <Icon icon="solar:check-circle-linear" width={14} />
                          </Button>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Mark as read</Tooltip.Content>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <Tooltip.Trigger>
                        <Button
                         
                          size="sm"
                          variant="ghost"
                          onPress={() => handleDismiss(n._id)}
                          aria-label="Dismiss notification"
                          className="h-6 w-6 min-w-0"
                        >
                          <Icon icon="solar:close-circle-linear" width={14} />
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Content>Dismiss</Tooltip.Content>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollShadow>

        {/* Footer */}
        {visibleNotifications.length > 0 && (
          <div className="flex-none border-t border-border px-4 py-2 text-center">
            <button
              type="button"
              onClick={() => loadNotifications()}
              className="text-xs text-muted hover:text-muted transition-colors"
            >
              Refresh
            </button>
          </div>
        )}
        </div>
      </Popover.Content>
    </Popover>
    
    {/* Push Notification Settings Modal */}
    <PushNotificationSettings isOpen={isSettingsOpen} onClose={onSettingsClose} />
    </>
  );
}
